import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { payWithMidtrans } from "@/lib/midtrans";
import { formatRp, parseRupiah, formatRupiahInput } from "@/lib/constants";
import { CreditCard, ArrowsClockwise } from "@phosphor-icons/react";

const inp = "w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-[#7C4DE0]";

const STATUS_LABEL = {
  settlement: "Lunas", capture: "Terbayar", pending: "Menunggu", deny: "Ditolak",
  cancel: "Dibatalkan", expire: "Kedaluwarsa", token_error: "Gagal Token",
};

// Snap payment (Sandbox) for an order or reservation. Auto-hides when Midtrans is not configured.
// defaultAmount lets the parent prefill the DP nominal (not the full outstanding) when the
// payment mode is DP; falls back to the full remaining balance for Lunas/pelunasan.
export default function MidtransPayBlock({ kind, id, sisa, onPaid, isAdmin, defaultAmount }) {
  const roundedSisa = Math.round(sisa || 0);
  const defAmt = Math.round((defaultAmount != null ? defaultAmount : sisa) || 0);
  const isDpDefault = defAmt > 0 && defAmt < roundedSisa;
  const [enabled, setEnabled] = useState(false);
  const [isProd, setIsProd] = useState(false);
  const [amount, setAmount] = useState(formatRupiahInput(String(defAmt)));
  const [busy, setBusy] = useState(false);
  const [checking, setChecking] = useState(false);
  const [attempts, setAttempts] = useState(null);

  // Keep the prefill in sync when the parent's DP/outstanding figures change (e.g. after a payment).
  useEffect(() => {
    setAmount(formatRupiahInput(String(defAmt)));
  }, [defAmt]);

  useEffect(() => {
    api.get("/payments/midtrans/config")
      .then(({ data }) => { setEnabled(!!data.configured); setIsProd(!!data.is_production); })
      .catch(() => setEnabled(false));
  }, []);

  const pay = useCallback(async () => {
    if (busy) return; // prevent double-click
    const amt = parseRupiah(amount);
    if (!amt || amt <= 0) return toast.error("Nominal tidak valid");
    if (amt > Math.round(sisa)) return toast.error("Nominal melebihi sisa tagihan");
    setBusy(true);
    try {
      const res = await payWithMidtrans({ kind, id, amount: amt });
      if (res.status === "success") { toast.success("Pembayaran Midtrans berhasil"); onPaid && onPaid(); }
      else if (res.status === "pending") { toast.info("Pembayaran menunggu penyelesaian"); onPaid && onPaid(); }
      else if (res.status === "error") { toast.error("Pembayaran gagal / ditolak"); }
      // "closed": user closed the popup, do nothing
    } catch (e) { toast.error(apiError(e) || e.message); }
    finally { setBusy(false); }
  }, [busy, amount, kind, id, sisa, onPaid]);

  const checkStatus = useCallback(async () => {
    if (checking) return;
    setChecking(true);
    try {
      const { data } = await api.get(`/payments/midtrans/attempts/${kind}/${id}`);
      for (const a of data) {
        if (["pending", "capture"].includes(a.status)) {
          try { await api.post(`/payments/midtrans/${a.midtrans_order_id}/reconcile`); } catch (_) { /* keep going */ }
        }
      }
      const refreshed = await api.get(`/payments/midtrans/attempts/${kind}/${id}`);
      setAttempts(refreshed.data);
      toast.success("Status Midtrans diperbarui");
      onPaid && onPaid();
    } catch (e) { toast.error(apiError(e)); }
    finally { setChecking(false); }
  }, [checking, kind, id, onPaid]);

  if (!isAdmin) return null; // Admin/Super Admin only — never shown to patients/public
  if (!enabled) return null;

  return (
    <div className="mt-3 rounded-xl border border-[#C7D2FE] bg-[#EEF2FF] p-3" data-testid="midtrans-block">
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <p className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-indigo-700">
          <CreditCard size={14} weight="bold" /> Bayar via Midtrans
        </p>
        <span data-testid="midtrans-mode-badge"
          className={`rounded-full px-2 py-0.5 text-[10px] font-extrabold uppercase tracking-wide ${isProd ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-800 ring-1 ring-amber-300"}`}>
          {isProd ? "Live" : "Sandbox / Test"}
        </span>
      </div>
      {!isProd && (
        <p className="mb-2 rounded-lg bg-amber-50 px-2 py-1.5 text-[11px] font-semibold text-amber-700" data-testid="midtrans-sandbox-note">
          Mode uji (SANDBOX) — gunakan kartu/simulator Midtrans, bukan pembayaran nyata.
        </p>
      )}
      {sisa > 0 && (
        <div className="flex gap-2">
          <input data-testid="midtrans-amount" inputMode="numeric" value={amount}
            onChange={(e) => setAmount(formatRupiahInput(e.target.value))} placeholder="Nominal" className={inp} />
          <button data-testid="midtrans-pay-btn" onClick={pay} disabled={busy}
            className="shrink-0 rounded-xl bg-indigo-600 px-4 py-2 text-sm font-bold text-white hover:bg-indigo-700 disabled:opacity-50">
            {busy ? "Memproses…" : "Bayar"}
          </button>
        </div>
      )}
      {sisa > 0 && isDpDefault && (
        <p className="mt-1 text-[11px] font-medium text-indigo-700/80" data-testid="midtrans-dp-hint">
          Default DP: {formatRp(defAmt)} · Sisa tagihan: {formatRp(roundedSisa)} — nominal bisa diubah (maks. sisa tagihan).
        </p>
      )}
      {isAdmin && (
        <button data-testid="midtrans-check-btn" onClick={checkStatus} disabled={checking}
          className="mt-2 inline-flex items-center gap-1.5 rounded-xl border border-indigo-300 px-3 py-2 text-xs font-bold text-indigo-700 hover:bg-indigo-100 disabled:opacity-50">
          <ArrowsClockwise size={13} weight="bold" className={checking ? "animate-spin" : ""} /> Cek Status Midtrans
        </button>
      )}
      {attempts && attempts.length > 0 && (
        <div className="mt-2 space-y-1" data-testid="midtrans-attempts">
          {attempts.map((a) => (
            <div key={a.midtrans_order_id} className="flex items-center justify-between text-[11px] text-slate-600">
              <span>{formatRp(a.amount)} · {String(a.created_at).slice(0, 16).replace("T", " ")}</span>
              <span className="font-bold text-indigo-700">{STATUS_LABEL[a.status] || a.status}</span>
            </div>
          ))}
        </div>
      )}
      {attempts && attempts.length === 0 && (
        <p className="mt-2 text-[11px] text-slate-500" data-testid="midtrans-attempts-empty">Belum ada transaksi Midtrans.</p>
      )}
    </div>
  );
}
